import {
  createInstance,
  errorHandler,
  OptimizelyDecideOption,
} from '@optimizely/optimizely-sdk'
import { OPTIMIZELY_COOKIE_NAME } from '@awaze/mfe-common.lib.optimizely'
import { OptimizelyAttributes } from './types'
import { OPTIMIZELY_FLAGS_ENUM } from './optimizely-const'

const optimizelyClient = createInstance({
  sdkKey: process.env.OPTIMIZELY_SDK_KEY,
  errorHandler,
})

const createUserContext = async (
  userId: string,
  attributes: OptimizelyAttributes,
) => {
  try {
    const result = await optimizelyClient?.onReady()

    if (!result?.success) {
      throw new Error(
        'Optimizely client failed to initialize, reason:' + result?.reason,
      )
    }

    const user = optimizelyClient?.createUserContext(userId, attributes)

    if (!user) {
      throw new Error('Failed to create Optimizely user context')
    }

    return user
  } catch (error) {
    console.error('Error creating Optimizely user context:', error)
  }
}

export const getTMSProxyEndpointDecision = async (
  cookies: Record<string, string>,
  attributes: OptimizelyAttributes,
): Promise<boolean> => {
  const user = await createUserContext(
    cookies[OPTIMIZELY_COOKIE_NAME],
    attributes,
  )

  if (!user) {
    return false
  }

  const decision = user.decide(
    OPTIMIZELY_FLAGS_ENUM.TMS_GATEWAY_PROXY_ENDPOINT,
    [OptimizelyDecideOption.INCLUDE_REASONS],
  )

  return decision.variationKey === 'on'
}

export const getWishlistIconDecision = async (
  cookies: Record<string, string>,
  attributes: OptimizelyAttributes,
  headerData: {
    desktopMenu?: {
      menuItems?: Array<{ type?: string; link?: { href?: string } }>
    }
  } | null,
): Promise<boolean> => {
  const user = await createUserContext(
    cookies[OPTIMIZELY_COOKIE_NAME],
    attributes,
  )

  if (!user) {
    return false
  }

  const decision = user.decide(OPTIMIZELY_FLAGS_ENUM.WISHLIST_ENTRY_POINTS, [
    OptimizelyDecideOption.INCLUDE_REASONS,
  ])

  // Check if wishlist icon exists in header data and experiment decision
  const wishListIcon = headerData?.desktopMenu?.menuItems?.find(
    (item) => item.type === 'wishlist',
  )

  return (
    !!wishListIcon &&
    !!wishListIcon.link?.href &&
    decision.variationKey === 'wishlist_entry_points_visible'
  )
}
